import { Editor } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import type { Node as ProseMirrorNode, Schema } from '@tiptap/pm/model';

export type ListKind = 'bulletList' | 'orderedList' | 'taskList';

const LIST_NODE_NAMES: ReadonlySet<string> = new Set(['bulletList', 'orderedList', 'taskList']);

const ITEM_NODE_NAME: Record<ListKind, string> = {
  bulletList: 'listItem',
  orderedList: 'listItem',
  taskList: 'taskItem',
};

interface EnclosingList {
  listNode: ProseMirrorNode;
  listPos: number;
}

/**
 * Finds the list node enclosing the current selection. Returns null if the
 * selection isn't inside a list, or if it spans past the end of the
 * innermost enclosing list (ambiguous - caller should fall back to
 * creating a fresh list instead of converting).
 */
export function findEnclosingListRange(editor: Editor): EnclosingList | null {
  const { $from, $to } = editor.state.selection;

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    if (LIST_NODE_NAMES.has(node.type.name)) {
      const listPos = $from.before(depth);
      const listEnd = listPos + node.nodeSize;
      return $to.pos <= listEnd ? { listNode: node, listPos } : null;
    }
  }

  return null;
}

/**
 * Rebuilds a list node as a different list kind, remapping each direct item
 * to the target item type. Reuses each item's existing content Fragment
 * unchanged - valid because listItem and taskItem (configured `nested:
 * true`) both have content 'paragraph block*', so nested sub-lists inside
 * an item survive untouched. Sets checked:false entering a task list, drops
 * checked otherwise. Preserves orderedList attrs (e.g. start) when
 * converting into an ordered list.
 */
export function buildConvertedListNode(
  listNode: ProseMirrorNode,
  targetKind: ListKind,
  schema: Schema,
): ProseMirrorNode | null {
  const targetListType = schema.nodes[targetKind];
  const targetItemType = schema.nodes[ITEM_NODE_NAME[targetKind]];

  if (!targetListType || !targetItemType) {
    return null;
  }

  const newItems: ProseMirrorNode[] = [];
  listNode.forEach((item) => {
    const attrs: Record<string, unknown> = targetKind === 'taskList' ? { checked: item.attrs['checked'] === true } : {};
    newItems.push(targetItemType.create(attrs, item.content));
  });

  const listAttrs = targetKind === 'orderedList' ? listNode.attrs : null;
  return targetListType.create(listAttrs, Fragment.from(newItems));
}

/**
 * Converts the list enclosing the current selection to targetKind,
 * preserving content. Returns false (no-op) if the selection isn't cleanly
 * inside a single list - callers should fall back to their normal
 * create-a-fresh-list behavior in that case.
 */
export function convertListType(editor: Editor, targetKind: ListKind): boolean {
  const found = findEnclosingListRange(editor);
  if (!found) {
    return false;
  }

  const { listNode, listPos } = found;

  if (listNode.type.name === targetKind) {
    return editor.chain().focus().liftListItem(ITEM_NODE_NAME[targetKind]).run();
  }

  const newList = buildConvertedListNode(listNode, targetKind, editor.state.schema);
  if (!newList) {
    return false;
  }

  return editor
    .chain()
    .focus()
    .command(({ tr }) => {
      tr.replaceWith(listPos, listPos + listNode.nodeSize, newList);
      return true;
    })
    .run();
}

export function toggleTaskListSafely(editor: Editor): boolean {
  if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
    return convertListType(editor, 'taskList');
  }

  return editor.chain().focus().toggleTaskList().run();
}
